/**
 * API Error Parser Utility
 * 
 * Parses API error responses and extracts user-friendly messages.
 * Supports the standard error format:
 * {
 *   result: "error",
 *   error_code: 422,
 *   error_description: "Some of the data did not pass validation",
 *   error_details: { field: "Error message" },
 *   error_message: { field: "Error message" } | string,
 *   user_message: "Не верно указаные некоторые параметры"
 * }
 */

export interface ApiErrorData {
    result?: string
    error_code?: number
    error_description?: string
    error_details?: Record<string, string>
    error_message?: Record<string, string> | string
    user_message?: string
}

export interface ParsedApiError {
    /** Main error message to display */
    message: string
    /** Field-specific errors (field name -> error message) */
    fieldErrors: Record<string, string>
    /** Original error code */
    code?: number
}

/**
 * Parse API error response into a structured format
 */
export function parseApiError(error: any): ParsedApiError {
    const defaultMessage = 'Произошла ошибка. Попробуйте ещё раз.'

    // Try to get data from error response
    const data: ApiErrorData = error?.response?.data || error?.data || error || {}

    // Priority: user_message > error_description > default
    let message = defaultMessage
    if (data.user_message) {
        message = data.user_message
    } else if (data.error_description) {
        message = data.error_description
    }

    // Extract field errors from error_message (if it's an object)
    const fieldErrors: Record<string, string> = {}
    if (data.error_message && typeof data.error_message === 'object') {
        Object.assign(fieldErrors, data.error_message)
    } else if (data.error_details && typeof data.error_details === 'object') {
        Object.assign(fieldErrors, data.error_details)
    }

    return {
        message,
        fieldErrors,
        code: data.error_code
    }
}

/**
 * Format field errors as HTML list for display
 */
export function formatFieldErrors(fieldErrors: Record<string, string>): string {
    const entries = Object.entries(fieldErrors)
    if (entries.length === 0) return ''

    if (entries.length === 1) {
        return entries[0][1]
    }

    const items = entries.map(([, msg]) => `<li>${msg}</li>`).join('')
    return `<ul class="error-list">${items}</ul>`
}

/**
 * Get display message from API error (combines main message with field errors)
 */
export function getErrorMessage(error: any): string {
    const parsed = parseApiError(error)
    const fieldErrorsHtml = formatFieldErrors(parsed.fieldErrors)

    if (fieldErrorsHtml) {
        return `${parsed.message}<br/>${fieldErrorsHtml}`
    }

    return parsed.message
}
